import { isSupportedImageType, supportedImageMimeTypes } from "./images";

/**
 * Shown wherever we turn a file away for its type: the client-side upload
 * guard, the server-side upload rejection, and the pipeline's terminal
 * UNSUPPORTED_FILE_TYPE failure. One string so a user who trips two of them
 * doesn't get two different explanations of the same rule.
 */
export const UNSUPPORTED_FILE_TYPE_MESSAGE =
  "The file type is not supported. Please upload a PDF or image file (JPG, HEIC, PNG).";

/**
 * Bundled multi-file documents (case chat / home bill) may be many images or
 * exactly one PDF — mixing or stacking PDFs leaves the agent and document
 * viewer looking at only the first PDF.
 */
export const BUNDLED_UPLOAD_MIXED_TYPES_MESSAGE =
  "Upload either photos or a single PDF, not both.";

export const BUNDLED_UPLOAD_MULTI_PDF_MESSAGE = "Upload one PDF at a time.";

/**
 * Shown when a surface turns away a batch because another upload is already in
 * flight. Shared so every upload entry point names the same single-flight
 * guard the same way.
 */
export const UPLOAD_BUSY_MESSAGE =
  "Another upload is still going. Try again in a moment.";

const mediaType = (contentType: string): string => {
  const [raw = ""] = contentType.split(";", 1);
  return raw.trim().toLowerCase();
};

/**
 * A MIME we can act on. Anything else — blank, `application/octet-stream`, or
 * a wrong-but-specific label like `application/x-pdf` that some browsers emit
 * — tells us nothing, so the filename decides instead.
 */
const isKnownMime = (type: string): boolean =>
  type === "application/pdf" || isSupportedImageType(type);

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
  ".heif",
  ".webp",
] as const;

export type BundledUploadSelectionFile = {
  name?: string | null;
  contentType?: string | null;
};

const normalizedName = (name?: string | null): string =>
  name?.trim().toLowerCase() ?? "";

export const isPdfSelection = (file: BundledUploadSelectionFile): boolean => {
  const type = mediaType(file.contentType ?? "");
  if (type === "application/pdf") {
    return true;
  }
  if (isKnownMime(type)) {
    return false;
  }
  return normalizedName(file.name).endsWith(".pdf");
};

const isImageSelection = (file: BundledUploadSelectionFile): boolean => {
  const type = mediaType(file.contentType ?? "");
  if (isSupportedImageType(type)) {
    return true;
  }
  if (isKnownMime(type)) {
    return false;
  }
  const name = normalizedName(file.name);
  return IMAGE_EXTENSIONS.some(extension => name.endsWith(extension));
};

export type UploadAccept = "images" | "documents" | "both";

/**
 * Whether a surface will take this file at all — the question a file picker's
 * `accept` list answers for the OS dialog, and which a drop target has to
 * answer itself. Composed from the same two predicates the bundling rules use,
 * so "what counts as a PDF or an image" is defined once.
 */
export function isAcceptedUploadFile(
  file: BundledUploadSelectionFile,
  accept: UploadAccept = "both",
): boolean {
  return (
    (accept !== "images" && isPdfSelection(file)) ||
    (accept !== "documents" && isImageSelection(file))
  );
}

/**
 * The MIME types a surface takes, for a picker that has to tell the OS dialog
 * what to offer before there is a file to test. Derived from the allowlist
 * `isAcceptedUploadFile` reads, so a dialog cannot grey out a file the drop
 * target next to it would have accepted.
 */
export const acceptedUploadMimeTypes = (
  accept: UploadAccept = "both",
): string[] => [
  ...(accept === "images" ? [] : ["application/pdf"]),
  ...(accept === "documents" ? [] : supportedImageMimeTypes),
];

/**
 * Composition error for one multi-file document batch, or `undefined` when
 * the selection is allowed. Single-file batches (including FA's one-file-per-
 * document uploads) always pass. When the MIME isn't one we recognise, falls
 * back to the filename the same way `isArchiveFile` does.
 */
export function getBundledUploadSelectionError(
  files: readonly BundledUploadSelectionFile[],
): string | undefined {
  if (files.length <= 1) {
    return undefined;
  }

  let pdfCount = 0;
  let imageCount = 0;
  for (const file of files) {
    if (isPdfSelection(file)) {
      pdfCount += 1;
      continue;
    }
    if (isImageSelection(file)) {
      imageCount += 1;
    }
  }

  if (pdfCount > 0 && imageCount > 0) {
    return BUNDLED_UPLOAD_MIXED_TYPES_MESSAGE;
  }
  if (pdfCount > 1) {
    return BUNDLED_UPLOAD_MULTI_PDF_MESSAGE;
  }
  return undefined;
}

/**
 * Archive content types the document pipeline can never process. Shared by
 * the API's upload validation and the client-side upload guard so the lists
 * can't drift. Kept as a blocklist (not an allowlist) because staff and
 * ingestion flows legitimately create documents with content types we can't
 * enumerate — the analysis pipeline has its own guard for everything else.
 */
const ARCHIVE_CONTENT_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-7z-compressed",
  "application/x-rar-compressed",
  "application/vnd.rar",
  "application/gzip",
  "application/x-gzip",
  "application/x-tar",
  "application/x-bzip2",
]);

export function isArchiveContentType(contentType: string): boolean {
  // The value is an arbitrary client-supplied string, so strip MIME
  // parameters ("application/zip; charset=binary") before matching.
  const [mediaType = ""] = contentType.split(";", 1);
  return ARCHIVE_CONTENT_TYPES.has(mediaType.trim().toLowerCase());
}

/**
 * Extension fallback for when the content type tells us nothing. Browsers
 * report "" or application/octet-stream whenever the OS can't identify a
 * file, which is common for the compressed formats above, and we can't
 * blocklist octet-stream itself without rejecting legitimate uploads.
 */
const ARCHIVE_EXTENSIONS = [
  ".zip",
  ".7z",
  ".rar",
  ".gz",
  ".tgz",
  ".tar",
  ".bz2",
  ".tbz2",
];

/**
 * Whether an upload is an archive by either signal. Prefer this over
 * `isArchiveContentType` anywhere a file name is available.
 */
export function isArchiveFile({
  name,
  contentType,
}: {
  name?: string | null;
  contentType?: string | null;
}): boolean {
  if (contentType && isArchiveContentType(contentType)) {
    return true;
  }
  const normalized = name?.trim().toLowerCase() ?? "";
  return ARCHIVE_EXTENSIONS.some(extension => normalized.endsWith(extension));
}
